import {
  commitUpdateIssueProjectsMutation,
  LazyProjectPicker,
  type ProjectPickerProject$data as Project,
  type ProjectPickerProject$key,
  ProjectPickerProjectFragment,
} from '@github-ui/item-picker/project'
import {useToastContext} from '@github-ui/toast'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {useCallback, useMemo, Suspense, useState} from 'react'
import {
  graphql,
  type PreloadedQuery,
  readInlineData,
  usePreloadedQuery,
  useRelayEnvironment,
  useFragment,
} from 'react-relay'
import {ConnectionHandler} from 'relay-runtime'

import {ERRORS, LABELS} from '../../constants'
import {commitDeleteIssueProjectsMutation} from '../../mutations/delete-issue-projects-mutation'

import type {ProjectsSectionQuery} from './__generated__/ProjectsSectionQuery.graphql'
import {ProjectItemSection} from './ProjectItemSection'
import {ReadonlySectionHeader} from './ReadonlySectionHeader'
import {Section} from './Section'
import type {StyledHeadingProps} from './SectionHeader'
import {SectionHeader} from './SectionHeader'
import type {ProjectsSectionFragment$key} from './__generated__/ProjectsSectionFragment.graphql'
import {Box, Button, SelectPanel} from '@primer/react'
import {noop} from '@github-ui/noop'
import {useKeyPress} from '@github-ui/use-key-press'
import {HOTKEYS} from '@github-ui/item-picker'
import {ChevronDownIcon, ChevronUpIcon} from '@primer/octicons-react'

type ProjectsSectionBaseProps = {
  selectedProjectId?: string
  allowedProjectOwner?: string
  onIssueUpdate?: () => void
  headerSx?: BetterSystemStyleObject
  sectionSx?: BetterSystemStyleObject
  headerProps?: StyledHeadingProps
  singleKeyShortcutsEnabled: boolean
  prefetchInteractionDataEnabled: boolean
}

type ProjectsSectionProps = ProjectsSectionBaseProps & {
  projectsSectionRef: PreloadedQuery<ProjectsSectionQuery>
}

type ProjectsSectionInternalProps = ProjectsSectionBaseProps & {
  issueOrPullRequest: ProjectsSectionFragment$key
}

export const ProjectsSectionGraphqlQuery = graphql`
  query ProjectsSectionQuery($owner: String!, $repo: String!, $number: Int!) {
    repository(owner: $owner, name: $repo) {
      issueOrPullRequest(number: $number) {
        ...ProjectsSectionFragment
      }
    }
  }
`

const projectSectionFragment = graphql`
  fragment ProjectsSectionFragment on IssueOrPullRequest
  @argumentDefinitions(allowedOwner: {type: "String", defaultValue: null}) {
    ... on Issue {
      number
      repository {
        name
        owner {
          login
        }
      }
      id
      projectItemsNext(first: 10, allowedOwner: $allowedOwner)
        @required(action: THROW)
        @connection(key: "ProjectSection_projectItemsNext") {
        edges {
          node {
            id
            ...ProjectItemSection
            project {
              ...ProjectPickerProject
              closed
              title
              id
            }
          }
        }
      }
      viewerCanUpdateNext
    }
    ... on PullRequest {
      number
      repository {
        name
        owner {
          login
        }
      }
      id
      projectItemsNext(first: 10, allowedOwner: $allowedOwner)
        @required(action: THROW)
        @connection(key: "ProjectSection_projectItemsNext") {
        edges {
          node {
            id
            ...ProjectItemSection
            project {
              closed
              ...ProjectPickerProject
              title
              id
            }
          }
        }
      }
      viewerCanUpdate
    }
  }
`

export function ProjectsSection({projectsSectionRef, ...rest}: ProjectsSectionProps) {
  const projectsSectionData = usePreloadedQuery<ProjectsSectionQuery>(ProjectsSectionGraphqlQuery, projectsSectionRef)
  return projectsSectionData?.repository?.issueOrPullRequest ? (
    <ProjectSectionInternal issueOrPullRequest={projectsSectionData?.repository?.issueOrPullRequest} {...rest} />
  ) : null
}

export function ProjectSectionInternal({
  issueOrPullRequest,
  onIssueUpdate,
  headerSx,
  sectionSx,
  headerProps,
  singleKeyShortcutsEnabled,
  selectedProjectId,
  allowedProjectOwner,
  prefetchInteractionDataEnabled,
}: ProjectsSectionInternalProps) {
  const {addToast} = useToastContext()
  const projectsSectionData = useFragment(projectSectionFragment, issueOrPullRequest)

  const projectItems = (projectsSectionData.projectItemsNext?.edges ?? [])
    .flatMap(edge => (edge?.node ? edge?.node : []))
    .filter(projectItem => projectItem.project.closed !== true)

  const closedProjectItems = (projectsSectionData.projectItemsNext?.edges ?? [])
    .flatMap(edge => (edge?.node ? edge?.node : []))
    .filter(projectItem => projectItem.project.closed === true)

  const [showClosedProjects, setShowClosedProjects] = useState(false)

  const readonly = !(projectsSectionData.viewerCanUpdateNext || projectsSectionData.viewerCanUpdate)

  const issueId = projectsSectionData.id || ''
  const hasProjectItems = projectItems.length > 0 || closedProjectItems.length > 0
  const connectionId = ConnectionHandler.getConnectionID(
    issueId, // passed as input to the mutation/subscription
    'ProjectSection_projectItemsNext',
    {
      allowedOwner: allowedProjectOwner,
    },
  )

  const environment = useRelayEnvironment()
  const saveProjects = useCallback(
    (selectedProjects: Project[]) => {
      const projectsToAdd = selectedProjects.filter(project => !projectItems.some(p => p.project.id === project.id))
      const projectsToRemove = projectItems.filter(project => !selectedProjects.some(p => p.id === project.project.id))

      let hasError = false
      for (const project of projectsToAdd) {
        commitUpdateIssueProjectsMutation({
          environment,
          connectionId,
          projectId: project.id,
          issueId,
          onError: () => (hasError = true),
          onCompleted: onIssueUpdate,
        })
      }

      for (const project of projectsToRemove) {
        commitDeleteIssueProjectsMutation({
          environment,
          connectionId,
          projectId: project.project.id,
          itemId: project.id,
          onError: () => (hasError = true),
          onCompleted: onIssueUpdate,
        })
      }

      if (hasError) {
        addToast({
          type: 'error',
          message: ERRORS.couldNotUpdateProjects,
        })
      }
    },
    [addToast, connectionId, environment, issueId, onIssueUpdate, projectItems],
  )

  const selectedProjects = useMemo(
    () =>
      projectItems.map(projectItem =>
        // eslint-disable-next-line no-restricted-syntax
        readInlineData<ProjectPickerProject$key>(ProjectPickerProjectFragment, projectItem.project),
      ),
    [projectItems],
  )

  const sectionHeader = useMemo(() => {
    if (!projectsSectionData.repository) return <></>
    if (readonly) return <ReadonlySectionHeader title={LABELS.sectionTitles.projects} />

    return prefetchInteractionDataEnabled ? (
      <Suspense
        fallback={
          <ReadonlySectionHeader title={LABELS.sectionTitles.projects} sx={headerSx} headingProps={headerProps} />
        }
      >
        <LazyProjectPicker
          pickerId="list-header-projects-picker"
          repo={projectsSectionData.repository.name}
          owner={projectsSectionData.repository.owner.login}
          shortcutEnabled={singleKeyShortcutsEnabled}
          onSave={saveProjects}
          selectedProjects={selectedProjects}
          firstSelectedProjectTitle={projectItems.length > 0 ? projectItems[0]!.project.title : ''}
          anchorElement={props => (
            <SectionHeader
              sx={headerSx}
              title={LABELS.sectionTitles.projects}
              buttonProps={props}
              headingProps={headerProps}
            />
          )}
        />
      </Suspense>
    ) : (
      <LazyProjectPickerOnClick
        repo={projectsSectionData.repository.name}
        owner={projectsSectionData.repository.owner.login}
        shortcutEnabled={singleKeyShortcutsEnabled}
        selectedProjects={selectedProjects}
        firstSelectedProjectTitle={projectItems.length > 0 ? projectItems[0]!.project.title : ''}
        onSave={saveProjects}
        headerSx={headerSx}
        headerProps={headerProps}
      />
    )
  }, [
    headerProps,
    headerSx,
    prefetchInteractionDataEnabled,
    projectItems,
    projectsSectionData.repository,
    readonly,
    saveProjects,
    selectedProjects,
    singleKeyShortcutsEnabled,
  ])

  return projectsSectionData.repository && projectsSectionData.number ? (
    <Section
      id={`sidebar-projects-section`}
      sectionHeader={sectionHeader}
      sx={sectionSx}
      emptyText={hasProjectItems ? undefined : LABELS.emptySections.projects}
    >
      <Box sx={{pl: 2, pr: 1, width: '100%'}}>
        {projectItems.map(projectItem => (
          <ProjectItemSection
            key={projectItem.id}
            onIssueUpdate={onIssueUpdate}
            projectItem={projectItem}
            isOpen={selectedProjectId === projectItem.project.id}
          />
        ))}
        {closedProjectItems.length > 0 && (
          <>
            <Button
              variant={'invisible'}
              trailingVisual={showClosedProjects ? ChevronUpIcon : ChevronDownIcon}
              sx={{color: 'fg.subtle', p: 0, fontSize: '12px', '&:hover': {background: 'transparent !important'}}}
              onClick={() => setShowClosedProjects(!showClosedProjects)}
              aria-label={LABELS.showClosedProjects}
            >
              {`${closedProjectItems.length} closed project`}
            </Button>
            {showClosedProjects &&
              closedProjectItems.map(projectItem => (
                <ProjectItemSection
                  key={projectItem.id}
                  onIssueUpdate={onIssueUpdate}
                  projectItem={projectItem}
                  isOpen={selectedProjectId === projectItem.project.id}
                />
              ))}
          </>
        )}
      </Box>
    </Section>
  ) : (
    <></>
  )
}

type LazyProjectPickerOnClickProps = {
  repo: string
  owner: string
  shortcutEnabled: boolean
  selectedProjects: Project[]
  firstSelectedProjectTitle: string
  onSave: (selectedProjects: Project[]) => void
  headerSx?: BetterSystemStyleObject
  headerProps?: StyledHeadingProps
}

function LazyProjectPickerOnClick({
  repo,
  owner,
  shortcutEnabled,
  selectedProjects,
  firstSelectedProjectTitle,
  onSave,
  headerSx,
  headerProps,
}: LazyProjectPickerOnClickProps) {
  const [wasTriggered, setWasTriggered] = useState(false)

  useKeyPress(shortcutEnabled ? [HOTKEYS.projectPicker] : [], (e: KeyboardEvent) => {
    if (!wasTriggered) {
      setWasTriggered(true)
      e.preventDefault()
    }
  })

  if (!wasTriggered) {
    return (
      <SectionHeader
        sx={headerSx}
        title={LABELS.sectionTitles.projects}
        buttonProps={{onClick: () => setWasTriggered(true)}}
        headingProps={headerProps}
      />
    )
  }
  return (
    <Suspense
      fallback={
        <SelectPanel
          loading={true}
          placeholderText={LABELS.filterProjects}
          open
          onOpenChange={noop}
          onSelectedChange={noop}
          items={[]}
          onFilterChange={noop}
          selected={undefined}
          renderAnchor={props => <SectionHeader title={LABELS.sectionTitles.projects} buttonProps={props} />}
          overlayProps={{width: 'medium', height: 'auto'}}
          title={LABELS.selectProjects}
        />
      }
    >
      <LazyProjectPicker
        pickerId="list-header-projects-picker"
        repo={repo}
        owner={owner}
        shortcutEnabled={shortcutEnabled}
        onSave={onSave}
        triggerOpen={true}
        selectedProjects={selectedProjects}
        firstSelectedProjectTitle={firstSelectedProjectTitle}
        anchorElement={props => (
          <SectionHeader
            sx={headerSx}
            title={LABELS.sectionTitles.projects}
            buttonProps={props}
            headingProps={headerProps}
          />
        )}
      />
    </Suspense>
  )
}

try{ ProjectsSection.displayName ||= 'ProjectsSection' } catch {}
try{ ProjectSectionInternal.displayName ||= 'ProjectSectionInternal' } catch {}
try{ LazyProjectPickerOnClick.displayName ||= 'LazyProjectPickerOnClick' } catch {}